import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Divider,
  Paper,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Discount as DiscountIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ClientPaymentService from '../../services/clientPayment.service';
import ClientService from '../../services/client.service';
import PaymentTypeService from '../../services/paymentType.service';
import DescountService from '../../services/descount.service';
import { useTranslation } from '../../hooks/useTranslation';

const ClientPaymentAssign = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [descounts, setDescounts] = useState([]);
  const [existingPayments, setExistingPayments] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const [formData, setFormData] = useState({
    client_id: null,
    payment_type_id: null,
    descount_id: null,
    is_year_based: false,
    start_from_payment: 1,
    apply_mora_to_previous: false,
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [selectedDescount, setSelectedDescount] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.client_id) {
      fetchExistingPayments(formData.client_id);
    } else {
      setExistingPayments([]);
    }
  }, [formData.client_id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clientsRes, ptRes, descRes] = await Promise.all([
        ClientService.getClients(),
        PaymentTypeService.getAll(),
        DescountService.getAll(),
      ]);
      setClients(clientsRes.data?.data || clientsRes.data || []);
      setPaymentTypes(ptRes.data?.data || []);
      setDescounts(descRes.data?.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar(t('clientPayments.errorLoadingData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingPayments = async (clientId) => {
    setLoadingExisting(true);
    try {
      const response = await ClientPaymentService.getByClient(clientId);
      setExistingPayments(response.data?.data || []);
    } catch {
      setExistingPayments([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_id || !formData.payment_type_id) {
      showSnackbar(t('clientPayments.pleaseFillRequired'), 'error');
      return;
    }

    try {
      setSaving(true);
      const start = new Date();
      const endDate = new Date(start);
      endDate.setFullYear(endDate.getFullYear() + 1);
      const payload = {
        ...formData,
        descount_id: formData.descount_id || null,
        start: start.toISOString().slice(0, 10),
        end_date: endDate.toISOString().slice(0, 10),
        next_payment_date: start.toISOString().slice(0, 10),
        is_year_based: Boolean(formData.is_year_based),
        start_from_payment: formData.is_year_based ? (formData.start_from_payment || 1) : 1,
        apply_mora_to_previous: Boolean(formData.apply_mora_to_previous),
      };
      await ClientPaymentService.create(payload);
      showSnackbar(t('clientPayments.createdSuccess'));
      setTimeout(() => navigate('/memberships'), 1200);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        (error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(', ')
          : t('clientPayments.errorSaving'));
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const statusColors = {
    Active: 'success',
    suspended: 'warning',
    ended: 'error',
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/memberships')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" fontWeight={600}>
                {t('clientPayments.assignNew')}
              </Typography>
            </Box>
          }
          action={
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={saving}
              sx={{ mr: 1 }}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t('clientPayments.assignDescription')}
          </Typography>

          <Grid container spacing={3}>
            {/* Client Selection */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('clientPayments.selectClient')}
                </Typography>
              </Box>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => option.name || ''}
                value={selectedClient}
                onChange={(_, value) => {
                  setSelectedClient(value);
                  setFormData((prev) => ({ ...prev, client_id: value?.id || null }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('clientPayments.searchClient')}
                    size="small"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            {/* Existing memberships for selected client */}
            {formData.client_id && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    {t('clientPayments.existingMemberships')}
                  </Typography>
                  {loadingExisting ? (
                    <CircularProgress size={20} />
                  ) : existingPayments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('clientPayments.noExistingMemberships')}
                    </Typography>
                  ) : (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {existingPayments.map((ep) => (
                        <Chip
                          key={ep.id}
                          label={`${ep.payment_type?.name || '?'} — ${t(`clientPayments.status${ep.status?.charAt(0).toUpperCase() + ep.status?.slice(1)}`)}`}
                          color={statusColors[ep.status] || 'default'}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Payment Type */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PaymentIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('clientPayments.paymentType')}
                </Typography>
              </Box>
              <Autocomplete
                options={paymentTypes}
                getOptionLabel={(option) =>
                  `${option.name} — $${option.monto}`
                }
                value={selectedPaymentType}
                onChange={(_, value) => {
                  setSelectedPaymentType(value);
                  setFormData((prev) => ({ ...prev, payment_type_id: value?.id || null }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('clientPayments.selectPaymentType')}
                    size="small"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              {selectedPaymentType && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, backgroundColor: 'primary.50', borderRadius: 1 }}>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <Typography variant="caption">
                      <strong>{t('paymentTypes.amount')}:</strong> ${selectedPaymentType.monto}
                    </Typography>
                    <Typography variant="caption">
                      <strong>{t('paymentTypes.installments')}:</strong>{' '}
                      {selectedPaymentType.indefinite || selectedPaymentType.cuotas === 0
                        ? t('paymentTypes.indefiniteLabel')
                        : selectedPaymentType.cuotas}
                    </Typography>
                    <Typography variant="caption">
                      <strong>{t('paymentTypes.frequency')}:</strong> {selectedPaymentType.frecuencia}
                    </Typography>
                    <Typography variant="caption">
                      <strong>{t('paymentTypes.lateFee')}:</strong> ${selectedPaymentType.mora}
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Grid>

            {/* Discount */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DiscountIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('clientPayments.discount')}
                </Typography>
              </Box>
              <Autocomplete
                options={descounts}
                getOptionLabel={(option) => {
                  const p = parseFloat(option.porcentaje);
                  const m = parseFloat(option.monto);
                  if (!Number.isNaN(p) && p > 0) return `${option.porcentaje}%`;
                  if (!Number.isNaN(m) && m > 0) return `$${option.monto}`;
                  return `#${option.id}`;
                }}
                value={selectedDescount}
                onChange={(_, value) => {
                  setSelectedDescount(value);
                  setFormData((prev) => ({ ...prev, descount_id: value?.id || null }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('clientPayments.selectDiscount')}
                    size="small"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            {/* Is year based */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.is_year_based)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_year_based: e.target.checked,
                        start_from_payment: 1,
                        apply_mora_to_previous: false,
                      }))
                    }
                    color="primary"
                  />
                }
                label={t('clientPayments.isYearBased')}
              />
            </Grid>

            {/* Year-based: start from which payment */}
            {formData.is_year_based && selectedPaymentType && !selectedPaymentType.indefinite && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('clientPayments.startFromPayment')}</InputLabel>
                    <Select
                      value={formData.start_from_payment || 1}
                      label={t('clientPayments.startFromPayment')}
                      onChange={(e) => {
                        const n = Number(e.target.value) || 1;
                        setFormData((prev) => ({
                          ...prev,
                          start_from_payment: n,
                          apply_mora_to_previous: n > 1 ? prev.apply_mora_to_previous : false,
                        }));
                      }}
                    >
                      <MenuItem value={1}>{t('clientPayments.startFromNow')}</MenuItem>
                      {Array.from(
                        { length: Math.max(0, (selectedPaymentType.cuotas || 1) - 1) },
                        (_, i) => i + 2
                      ).map((n) => (
                        <MenuItem key={n} value={n}>
                          {t('clientPayments.startFromPaymentNumber')}{n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.start_from_payment > 1 && (
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formData.apply_mora_to_previous)}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, apply_mora_to_previous: e.target.checked }))
                          }
                          color="primary"
                        />
                      }
                      label={t('clientPayments.applyMoraToPrevious')}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientPaymentAssign;
